import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useRealtimeEvent } from "../../lib/realtime";
import Portal from "../../components/Portal";
import { QrCode, Trash2, User as UserIcon, ScanLine } from "@/components/icons";

const FRONTEND = window.location.origin;

const emptyRow = () => ({
  role: "",
  full_name: "",
  cedula: "",
  phone: "",
  address: "",
  birth_date: null,
  gender: null,
  blood_type: null,
  ars_name: null,
  ars_policy_number: null,
});

const GUARDIAN_ROLES = [
  { value: "padre", label: "Padre" },
  { value: "madre", label: "Madre" },
  { value: "tutor", label: "Tutor" },
  { value: "tutora", label: "Tutora" },
  { value: "otro", label: "Otro" },
];

export default function NewPatientQR() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(emptyRow());
  const [g1, setG1] = useState(null); // guardian 1 or null
  const [g2, setG2] = useState(null);
  const [scanning, setScanning] = useState(null); // {who: 'patient'|'g1'|'g2', token, expiresAt}
  const [saving, setSaving] = useState(false);

  // Cuando el paciente/tutor termina de escanear, este handler lo recibe.
  useRealtimeEvent("scan:updated", (payload) => {
    if (!scanning || payload.token !== scanning.token) return;
    if (payload.status === "failed") {
      toast.error("No pudimos leer la foto. Pídele que la retome.");
      setScanning(null);
      return;
    }
    if (payload.status !== "extracted") return;
    const ex = payload.extracted || {};
    const merged = {
      full_name: ex.full_name || "",
      cedula: ex.cedula || "",
      birth_date: ex.birth_date || null,
      gender: ex.gender || null,
      address: ex.address || "",
      phone: ex.phone || "",
      blood_type: ex.blood_type || null,
      ars_name: ex.ars_name || null,
      ars_policy_number: ex.ars_policy_number || null,
    };
    if (scanning.who === "patient") {
      setPatient((p) => ({ ...p, ...merged }));
    } else if (scanning.who === "g1") {
      setG1((g) => ({ ...(g || emptyRow()), ...merged }));
    } else if (scanning.who === "g2") {
      setG2((g) => ({ ...(g || emptyRow()), ...merged }));
    }
    toast.success("Datos extraídos ✓");
    setScanning(null);
  });

  const openScan = async (who) => {
    try {
      const { data } = await api.post("/patient-scan/session");
      setScanning({
        who,
        token: data.token,
        expiresAt: data.expires_at,
        url: `${FRONTEND}/scan/${data.token}`,
      });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!patient.full_name.trim()) {
      toast.error("Falta el nombre del paciente");
      return;
    }
    setSaving(true);
    try {
      const guardians = [g1, g2]
        .filter((g) => g && g.full_name && g.role)
        .map((g) => ({
          role: g.role,
          full_name: g.full_name,
          cedula: g.cedula || null,
          phone: g.phone || null,
          address: g.address || null,
        }));
      const body = {
        full_name: patient.full_name,
        document_id: patient.cedula || null,
        birth_date: patient.birth_date || null,
        gender: patient.gender || null,
        phone: patient.phone || null,
        address: patient.address || null,
        blood_type: patient.blood_type || null,
        guardians,
      };
      const { data } = await api.post("/patients", body);
      toast.success("Paciente registrado ✓");
      // Enrutamos al DETALLE del paciente. La ruta /doctor/pacientes/:id
      // acepta rol 'secretary' por config del router — así el detalle
      // funciona en ambos roles.
      // Si el usuario tiene role='secretary' pero la ruta bloquea por
      // path prefix, usamos la ruta base según rol.
      const idPath =
        user?.role === "secretary"
          ? `/secretaria/pacientes`
          : `/doctor/pacientes/${data.id}`;
      navigate(idPath);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const canSave = !!patient.full_name.trim();

  return (
    <div className="space-y-5" data-testid="new-patient-qr">
      <div>
        <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
          Nuevo paciente
        </h1>
        <p className="text-sm text-[#1B405B]/70">
          Genera un código QR, el paciente lo escanea y la IA rellena los
          datos automáticamente.
        </p>
      </div>

      <PersonRow
        title="Paciente"
        subtitle="Foto del seguro médico (o cédula)"
        data={patient}
        onScan={() => openScan("patient")}
        onEdit={(v) => setPatient((p) => ({ ...p, ...v }))}
        showRole={false}
        primary
      />

      <GuardianRow
        title="Responsable 1"
        subtitle="Opcional — foto de cédula"
        data={g1}
        onScan={() => openScan("g1")}
        onEdit={(v) => setG1((g) => ({ ...(g || emptyRow()), ...v }))}
        onRemove={() => setG1(null)}
      />

      <GuardianRow
        title="Responsable 2"
        subtitle="Opcional — foto de cédula"
        data={g2}
        onScan={() => openScan("g2")}
        onEdit={(v) => setG2((g) => ({ ...(g || emptyRow()), ...v }))}
        onRemove={() => setG2(null)}
      />

      <div className="pt-2">
        <button
          type="button"
          onClick={save}
          disabled={!canSave || saving}
          data-testid="save-patient-btn"
          className={`px-6 py-3 rounded-xl text-sm font-bold ${
            canSave
              ? "bg-[#01BC8E] text-white hover:bg-[#019976]"
              : "bg-[#F5F5F5] text-black/40 cursor-not-allowed"
          }`}
        >
          {saving ? "Guardando…" : "Registrar paciente"}
        </button>
      </div>

      {scanning && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full border border-black/10 text-center">
              <h3 className="text-xl font-black text-[#163144] mb-2">
                Muéstrale este código
              </h3>
              <p className="text-sm text-[#1B405B]/70 mb-4">
                Que lo escanee con la cámara de su celular. La foto se toma
                allí y los datos aparecerán aquí automáticamente.
              </p>
              <div className="p-4 bg-white inline-block rounded-2xl border border-black/10">
                <QRCodeSVG value={scanning.url} size={240} />
              </div>
              <p className="text-[11px] text-black/50 mt-3 break-all">
                {scanning.url}
              </p>
              <div className="text-xs text-amber-700 mt-3">
                🕒 El código expira en 10 min
              </div>
              <button
                type="button"
                onClick={() => setScanning(null)}
                className="mt-6 px-5 py-2 rounded-full text-sm font-bold bg-[#F5F5F5] text-[#163144]"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

function PersonRow({ title, subtitle, data, onScan, onEdit, primary }) {
  const filled = !!data.full_name;
  return (
    <div
      className={`bg-white rounded-[28px] border p-5 ${
        primary ? "border-[#01BC8E]/50" : "border-black/[0.06]"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              filled ? "bg-blue-50 text-[#019A75]" : "bg-black/5 text-black/50"
            }`}
          >
            <UserIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-black text-[#163144]">{title}</div>
            <div className="text-xs text-black/55">{subtitle}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onScan}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#01BC8E] text-white text-xs font-bold hover:bg-[#019976]"
          data-testid={`scan-${title.toLowerCase().replace(/\s/g, "-")}-btn`}
        >
          <ScanLine className="w-4 h-4" /> {filled ? "Reescanear" : "Escanear"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nombre completo" value={data.full_name} onChange={(v) => onEdit({ full_name: v })} />
        <Field label="Cédula" value={data.cedula} onChange={(v) => onEdit({ cedula: v })} />
        <Field label="Teléfono" value={data.phone} onChange={(v) => onEdit({ phone: v })} />
        <Field label="Fecha nacimiento" type="date" value={data.birth_date || ""} onChange={(v) => onEdit({ birth_date: v })} />
        <Field label="Tipo de sangre" value={data.blood_type || ""} onChange={(v) => onEdit({ blood_type: v })} />
        <Field label="Sexo" value={data.gender || ""} onChange={(v) => onEdit({ gender: v })} />
        <div className="sm:col-span-2">
          <Field label="Dirección" value={data.address} onChange={(v) => onEdit({ address: v })} />
        </div>
        {data.ars_name && (
          <div className="sm:col-span-2 flex items-center gap-2 text-xs bg-blue-50 rounded-xl p-2 text-emerald-800">
            <b>Seguro:</b> {data.ars_name} · Póliza {data.ars_policy_number || "—"}
          </div>
        )}
      </div>
    </div>
  );
}

function GuardianRow({ title, subtitle, data, onScan, onEdit, onRemove }) {
  const filled = !!(data && data.full_name);
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              filled ? "bg-blue-50 text-[#019A75]" : "bg-black/5 text-black/50"
            }`}
          >
            <UserIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-black text-[#163144]">{title}</div>
            <div className="text-xs text-black/55">{subtitle}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {filled && (
            <button
              type="button"
              onClick={onRemove}
              className="w-9 h-9 rounded-xl bg-black/5 text-black/60 hover:bg-black/10 inline-flex items-center justify-center"
              title="Quitar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onScan}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold ${
              filled ? "bg-white border border-black/10 text-[#163144]" : "bg-[#01BC8E] text-white hover:bg-[#019976]"
            }`}
          >
            <QrCode className="w-4 h-4" /> {filled ? "Reescanear" : "Escanear"}
          </button>
        </div>
      </div>

      {filled ? (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Rol
            </label>
            <select
              value={data.role}
              onChange={(e) => onEdit({ role: e.target.value })}
              className="mt-1 w-full glass-input rounded-xl px-3 py-2 text-sm"
            >
              <option value="">— Selecciona —</option>
              {GUARDIAN_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <Field label="Nombre completo" value={data.full_name} onChange={(v) => onEdit({ full_name: v })} />
          <Field label="Cédula" value={data.cedula} onChange={(v) => onEdit({ cedula: v })} />
          <Field label="Teléfono" value={data.phone} onChange={(v) => onEdit({ phone: v })} />
          <div className="sm:col-span-2">
            <Field label="Dirección" value={data.address} onChange={(v) => onEdit({ address: v })} />
          </div>
        </div>
      ) : (
        <p className="text-xs text-black/50">
          Genera un QR y pídele al padre / madre / tutor que lo escanee para
          agregar sus datos. Esta sección es completamente opcional.
        </p>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full glass-input rounded-xl px-3 py-2 text-sm"
      />
    </div>
  );
}
